/**
 * SalePaymentHistoryDialog — every payment applied to ONE sale, each with the
 * Proof of Payment it belongs to (owner 2026-09-22: "make a clickable show
 * payment history, then show the payment history modal").
 *
 * Props:
 *   open, onClose
 *   invoice        — the sale (needs invoice_number, payments[], amount_paid, balance, grand_total)
 *   onOpenPP(ppNumber) — a PP link was clicked; the caller opens ProofOfPaymentDialog
 */
import { formatPHP } from '../lib/utils';
import { formatDate } from '../lib/dateFormat';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { CreditCard, Ban } from 'lucide-react';

/** One plain sentence about how this sale was paid — for the link in Sale Detail. */
export function paymentSummaryLine(invoice) {
  const pays = (invoice?.payments || []).filter(p => p && Number(p.amount) > 0);
  const paid = Number(invoice?.amount_paid) || 0;
  const bal = Number(invoice?.balance) || 0;
  const onAccount = isOnAccount(invoice);
  if (!onAccount) {
    // cash / digital / check at the counter — the sale was paid when it was made
    const how = invoice?.payment_method && invoice.payment_method !== 'Cash' ? ` (${invoice.payment_method})` : '';
    return bal > 0.005 ? `Paid ${formatPHP(paid)} at the counter${how} · ${formatPHP(bal)} still open` : `Paid at the counter${how}`;
  }
  if (pays.length === 0) return paid > 0 ? `Paid ${formatPHP(paid)} · ${formatPHP(bal)} still open` : 'On account — no payments yet';
  const n = pays.length;
  const word = n === 1 ? '1 payment' : `${n} payments`;
  return bal > 0.005 ? `On account · ${word} so far · ${formatPHP(bal)} still open` : `On account · settled in ${word}`;
}

/** Credit / partial sales are settled later through Proofs of Payment; the rest was paid at the counter. */
export function isOnAccount(invoice) {
  const t = String(invoice?.payment_type || '').toLowerCase();
  return t === 'credit' || t === 'partial' || Number(invoice?.balance) > 0.005;
}

export default function SalePaymentHistoryDialog({ open, onClose, invoice, onOpenPP }) {
  const pays = (invoice?.payments || []).filter(Boolean);
  const total = pays.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sale-payment-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-bold text-slate-800">
            <CreditCard size={18} className="text-emerald-600" />
            Payment History <span className="font-mono text-slate-500 font-normal">{invoice?.invoice_number || ''}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {paymentSummaryLine(invoice)}. Click a Proof of Payment to see every receipt it settled.
          </DialogDescription>
        </DialogHeader>

        {pays.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8" data-testid="sale-payment-history-empty">No payments recorded on this sale.</p>
        ) : (
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-xs" data-testid="sale-payment-history-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Date</th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Method</th>
                  <th className="text-left px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Proof of Payment</th>
                  <th className="text-right px-3 py-2 text-[10px] font-semibold text-slate-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody>
                {pays.map((p, i) => (
                  <tr key={p.id || i} className="border-b border-slate-100 last:border-0" data-testid={`sale-payment-row-${i}`}>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                      {formatDate(p.date) || p.date || '—'}
                      {p.late_encoded && <span className="ml-1 text-[9px] text-amber-700">late encode</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {p.method && <Badge variant="outline" className="text-[10px]">{p.method}</Badge>}
                        {p.fund_source && p.fund_source !== 'discount' && <span className="text-slate-400">{p.fund_source}</span>}
                        {p.check_number && <span className="text-slate-500 font-mono">#{p.check_number}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {p.pp_number ? (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button onClick={() => onOpenPP?.(p.pp_number)}
                            className="font-mono text-blue-700 hover:underline" title="View Proof of Payment"
                            data-testid={`sale-payment-pp-${i}`}>
                            {p.pp_number}
                          </button>
                          {p.pp_voided && <Badge className="text-[9px] bg-red-100 text-red-700 border-0"><Ban size={9} className="mr-0.5" /> voided</Badge>}
                          {!p.pp_voided && p.pp_other_receipts > 0 && (
                            <span className="text-[10px] text-slate-400">
                              · also settled {p.pp_other_receipts} other receipt{p.pp_other_receipts === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-400" data-testid={`sale-payment-nopp-${i}`}>
                          {isOnAccount(invoice) ? 'no PP — recorded before proofs of payment existed' : 'paid at the counter — no PP'}
                        </span>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-right font-semibold ${p.fund_source === 'discount' ? 'text-blue-700' : 'text-emerald-700'}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatPHP(p.amount || 0)}
                      {p.fund_source === 'discount' && <div className="text-[9px] font-normal text-blue-600">discount</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr className="text-xs">
                  <td className="px-3 py-2 text-right font-semibold text-slate-600" colSpan={3}>Total applied</td>
                  <td className="px-3 py-2 text-right font-bold text-slate-800" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(total)}</td>
                </tr>
                {Number(invoice?.balance) > 0.005 && (
                  <tr className="text-xs">
                    <td className="px-3 py-2 text-right font-semibold text-red-600" colSpan={3}>Still open</td>
                    <td className="px-3 py-2 text-right font-bold text-red-600" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(invoice.balance)}</td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
